import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './CalendarPage.css';
import API_BASE from './apiBase';
import { authFetch } from './auth';
import { toDateKey } from './utils/dateUtils';

const MOODS = [
  { val: 1, emoji: '😢', label: 'Very Bad' },
  { val: 2, emoji: '😞', label: 'Bad' },
  { val: 3, emoji: '😐', label: 'Okay' },
  { val: 4, emoji: '😊', label: 'Good' },
  { val: 5, emoji: '😁', label: 'Great' },
];

const fmtWeekday = d => d.toLocaleDateString('en-US', { weekday: 'long' });
const fmtMonthDay = d => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const isTodayFn = d => toDateKey(d) === toDateKey(new Date());
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

function CalendarPage({ token }) {
  const nowTime = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayEntries, setDayEntries] = useState([]);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [mood, setMood] = useState(3);
  const [time, setTime] = useState(nowTime);
  const [toast, setToast] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [showCal, setShowCal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [expandedHistDays, setExpandedHistDays] = useState(new Set());
  const [modalEntry, setModalEntry] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/journal?start=1970-01-01`, {
        credentials: 'include',
      });
      const data = await res.json();
      setEntries(data.entries || []);
    } catch (_) {
      // Silently ignore; data shows as empty if unreachable.
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchEntries();
  }, [token, fetchEntries]);

  // Auto-compute day entries when entries load OR selectedDate changes —
  // ensures today's data appears immediately on mount without clicking.
  useEffect(() => {
    const iso = toDateKey(selectedDate);
    const filtered = entries.filter(e => e.date.slice(0, 10) === iso);
    setDayEntries(filtered.sort((a, b) => new Date(a.date) - new Date(b.date)));
  }, [entries, selectedDate]);

  const selectDate = (date) => {
    setSelectedDate(date);
    setText('');
    setTitle('');
    setMood(3);
    setTime(nowTime());
    setExpandedEntry(null);
  };

  const handleSave = async () => {
    if (!text.trim() && mood === 3) {
      showToast('Please add a note or select a mood', 'error');
      return;
    }
    const base = new Date(selectedDate);
    const [h, m] = time.split(':');
    base.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
    const iso = `${toDateKey(base)}T${String(base.getHours()).padStart(2, '0')}:${String(base.getMinutes()).padStart(2, '0')}:00`;
    const res = await authFetch(`${API_BASE}/api/journal`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: iso, title, text, mood }),
    });
    if (!res.ok) {
      const err = await res.text();
      showToast('Failed to save: ' + err, 'error');
      return;
    }
    const json = await res.json();
    const newEntry = { id: json.id, date: iso, title: title || '', text: text || '', mood: mood || 3 };
    setEntries(prev => [newEntry, ...prev]);
    setText('');
    setTitle('');
    setMood(3);
    setTime(nowTime());
    showToast('Entry saved');
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const res = await authFetch(`${API_BASE}/api/journal/${entryId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        showToast('Failed to delete', 'error');
        return;
      }
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setExpandedEntry(null);
      showToast('Entry deleted');
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
  };

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const iso = toDateKey(date);
    const dayEs = entries.filter(e => e.date.slice(0, 10) === iso);
    if (!dayEs.length) return null;
    const avg = dayEs.reduce((s, e) => s + (e.mood || 3), 0) / dayEs.length;
    const emoji = avg >= 4.5 ? '😁' : avg >= 3.5 ? '😊' : avg >= 2.5 ? '😐' : avg >= 1.5 ? '😞' : '😢';
    return <div className="jnl-tile-dot">{emoji}</div>;
  };

  // Group all entries by date for sidebar history
  const historyGroups = useMemo(() => {
    const map = {};
    entries.forEach(e => {
      const dk = e.date.slice(0, 10);
      if (!map[dk]) map[dk] = [];
      map[dk].push(e);
    });
    return Object.entries(map)
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([dk, es]) => ({
        dateKey: dk,
        label: new Date(dk + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
        isToday: dk === toDateKey(new Date()),
        entries: es.slice().sort((a, b) => new Date(a.date) - new Date(b.date)),
      }));
  }, [entries]);

  const toggleHistDay = (dk) => {
    setExpandedHistDays(prev => {
      const next = new Set(prev);
      if (next.has(dk)) next.delete(dk); else next.add(dk);
      return next;
    });
  };

  if (!token) {
    return <div className="jnl-page"><p className="jnl-empty-text">Please log in.</p></div>;
  }

  const moodObj = MOODS.find(m => m.val === mood) || MOODS[2];

  return (
    <div className="jnl-page">
      {toast && <div className={`toast toast--${toast.type}`}>{toast.msg}</div>}

      <div className="jnl-header">
        <div>
          <h2 className="jnl-title">Journal</h2>
          <p className="jnl-subtitle">{entries.length} {entries.length === 1 ? 'entry' : 'entries'} total</p>
        </div>
        <div className="jnl-header-actions">
          <button className="jnl-cal-toggle" onClick={() => setShowCal(v => !v)}>
            {showCal ? 'Hide calendar' : 'Show calendar'}
          </button>
          <button className="jnl-today-btn" onClick={() => selectDate(new Date())}>
            Today
          </button>
        </div>
      </div>

      <div className="jnl-body">
        {/* ── LEFT: calendar + day panel ── */}
        <div className="jnl-left">
          {showCal && (
            <div className="jnl-cal-wrap">
              <Calendar
                onClickDay={selectDate}
                tileContent={tileContent}
                value={selectedDate}
              />
            </div>
          )}

          <div className="jnl-day-panel">
            <div className="jnl-day-nav">
              <button className="jnl-nav-arrow" onClick={() => selectDate(addDays(selectedDate, -1))} aria-label="Previous day">‹</button>
              <div className="jnl-day-heading">
                <span className="jnl-day-weekday">{fmtWeekday(selectedDate)}</span>
                <span className="jnl-day-date">{fmtMonthDay(selectedDate)}</span>
                {isTodayFn(selectedDate) && <span className="jnl-today-badge">Today</span>}
              </div>
              <button className="jnl-nav-arrow" onClick={() => selectDate(addDays(selectedDate, 1))} aria-label="Next day">›</button>
            </div>

            {loading && <p className="jnl-loading">Loading…</p>}
            {!loading && dayEntries.length === 0 && (
              <div className="jnl-empty">
                <span className="jnl-empty-icon">✍️</span>
                <p>No entries for this day yet.</p>
              </div>
            )}

            <div className="jnl-entries">
              {dayEntries.map(e => {
                const isOpen = expandedEntry === e.id;
                const em = MOODS.find(m => m.val === e.mood) || MOODS[2];
                const displayTitle = e.title || (e.text ? e.text.slice(0, 52) + (e.text.length > 52 ? '…' : '') : 'Entry');
                const timeStr = new Date(e.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={e.id} className={`jnl-entry${isOpen ? ' jnl-entry--open' : ''}`}>
                    <button className="jnl-entry-header" onClick={() => setExpandedEntry(isOpen ? null : e.id)}>
                      <span className="jnl-entry-mood" title={em.label}>{em.emoji}</span>
                      <span className="jnl-entry-titletext">{displayTitle}</span>
                      <span className="jnl-entry-time">{timeStr}</span>
                      <span className="jnl-entry-chevron">{isOpen ? '▾' : '›'}</span>
                    </button>
                    {isOpen && (
                      <div className="jnl-entry-body">
                        {e.title && e.text && <div className="jnl-entry-full-title">{e.title}</div>}
                        {e.text && <p className="jnl-entry-text">{e.text}</p>}
                        <div className="jnl-entry-meta">
                          <span className="jnl-mood-chip">{em.emoji} {em.label}</span>
                          <button className="jnl-delete-btn" onClick={() => handleDelete(e.id)}>Delete</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="jnl-form">
              <div className="jnl-form-title">+ New entry</div>
              <div className="jnl-form-row">
                <input className="jnl-input" type="text" placeholder="Title (optional)" value={title} onChange={e => setTitle(e.target.value)} />
                <input className="jnl-input jnl-input--time" type="time" value={time} onChange={e => setTime(e.target.value)} />
              </div>
              <textarea className="jnl-textarea" placeholder="What's on your mind?" value={text} onChange={e => setText(e.target.value)} rows={3} />
              <div className="jnl-mood-row">
                <span className="jnl-mood-label-text">Mood:</span>
                {MOODS.map(m => (
                  <button key={m.val} type="button" className={`jnl-mood-btn${mood === m.val ? ' jnl-mood-btn--active' : ''}`} onClick={() => setMood(m.val)} title={m.label}>{m.emoji}</button>
                ))}
                <span className="jnl-mood-selected-label">{moodObj.label}</span>
              </div>
              <button className="jnl-save-btn" onClick={handleSave}>Save Entry</button>
            </div>
          </div>
        </div>

        {/* ── RIGHT: history sidebar ── */}
        <aside className="jnl-history">
          <div className="jnl-hist-header">
            <span className="jnl-hist-title">All Entries</span>
            <span className="jnl-hist-count">{historyGroups.length} {historyGroups.length === 1 ? 'day' : 'days'}</span>
          </div>
          {historyGroups.length === 0 && !loading && (
            <p className="jnl-hist-empty">No past entries yet.</p>
          )}
          <div className="jnl-hist-list">
            {historyGroups.map(g => {
              const isOpen = expandedHistDays.has(g.dateKey);
              const isSelected = toDateKey(selectedDate) === g.dateKey;
              return (
                <div key={g.dateKey} className={`jnl-hist-day${isSelected ? ' jnl-hist-day--active' : ''}`}>
                  <button
                    className="jnl-hist-day-btn"
                    onClick={() => {
                      selectDate(new Date(g.dateKey + 'T12:00:00'));
                      toggleHistDay(g.dateKey);
                    }}
                  >
                    <span className="jnl-hist-chevron">{isOpen ? '▾' : '▸'}</span>
                    <span className="jnl-hist-day-label">
                      {g.isToday && <span className="jnl-hist-today">Today · </span>}
                      {g.label}
                    </span>
                    <span className="jnl-hist-badge">{g.entries.length}</span>
                  </button>
                  {isOpen && (
                    <ul className="jnl-hist-entries">
                      {g.entries.map(e => {
                        const em = MOODS.find(m => m.val === e.mood) || MOODS[2];
                        const preview = e.title || (e.text ? e.text.slice(0, 48) + (e.text.length > 48 ? '…' : '') : 'Entry');
                        const t = new Date(e.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        return (
                          <li key={e.id} className="jnl-hist-entry" onClick={() => setModalEntry({ ...e, em, dayLabel: g.label })} role="button" tabIndex={0} onKeyDown={ev => ev.key === 'Enter' && setModalEntry({ ...e, em, dayLabel: g.label })}>
                            <span className="jnl-hist-entry-mood">{em.emoji}</span>
                            <span className="jnl-hist-entry-text">{preview}</span>
                            <span className="jnl-hist-entry-time">{t}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      {/* ── Entry detail modal ── */}
      {modalEntry && (() => {
        const dateStr = new Date(modalEntry.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        const timeStr = new Date(modalEntry.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return (
          <div className="jnl-modal-overlay" onClick={() => setModalEntry(null)}>
            <div className="jnl-modal" onClick={e => e.stopPropagation()}>
              <button className="jnl-modal-close" onClick={() => setModalEntry(null)} aria-label="Close">✕</button>
              <div className="jnl-modal-mood">{modalEntry.em.emoji}</div>
              <div className="jnl-modal-meta">
                <span className="jnl-modal-date">{dateStr}</span>
                <span className="jnl-modal-time">{timeStr}</span>
                <span className="jnl-modal-mood-label">{modalEntry.em.label}</span>
              </div>
              {modalEntry.title && <h3 className="jnl-modal-title">{modalEntry.title}</h3>}
              {modalEntry.text
                ? <p className="jnl-modal-body">{modalEntry.text}</p>
                : <p className="jnl-modal-body jnl-modal-body--empty">No note written for this entry.</p>
              }
              <div className="jnl-modal-actions">
                <button className="jnl-modal-delete" onClick={() => { handleDelete(modalEntry.id); setModalEntry(null); }}>Delete entry</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default CalendarPage;

